/**
 * @file AvailabilityCell.tsx
 * @description Колонка «Остаток» реестра товаров (phase19.14 W9; phase19.31 —
 * тоны кабинета вместо своих значков и капса).
 *
 * Правила W9:
 *  - «—» вместо нуля, когда складской учёт не ведётся (услуга, цифровой
 *    товар) или сводку остатков сервер не вернул: ноль читался бы как
 *    «кончился»;
 *  - ноль — «Нет» красным (SC26: ошибка — красный);
 *  - «мало» (0 < доступно ≤ порога фасовки) — янтарным;
 *  - при нескольких фасовках число раскрывает разбивку во всплывающем окне.
 */

import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { isProductLowStock, type CatalogProduct } from "../../lib/api-catalog";
import { plainQty } from "../../lib/catalog-format";
import { cn } from "@/lib/utils";

/** Цвет числа по тону (цвета тонов — из темы, SC27). */
const TONE_TEXT = {
  error: "font-medium text-[var(--status-error-fg,var(--destructive-text))]",
  attention: "font-medium text-[var(--status-attention-fg,var(--warning))]",
  normal: "text-on-surface",
} as const;

function toneOf(available: number, low: boolean): keyof typeof TONE_TEXT {
  if (available <= 0) return "error";
  return low ? "attention" : "normal";
}

interface Props {
  product: CatalogProduct;
  /** `false` — без всплывающей разбивки (ячейка внутри строки-ссылки телефона). */
  interactive?: boolean;
}

export function AvailabilityCell({ product, interactive = true }: Props) {
  if (product.stockTracked === undefined || product.stockTracked === false) {
    return (
      <span className="text-on-surface-variant" title={product.stockTracked === false ? "Складской учёт не ведётся" : undefined}>
        —
      </span>
    );
  }
  const available = Number(product.available ?? 0);
  const tone = toneOf(available, isProductLowStock(product));
  const skus = product.availableBySku ?? [];
  const body = (
    <span className={cn("whitespace-nowrap tabular-nums", TONE_TEXT[tone])}>
      {available <= 0 ? "Нет" : `${plainQty(product.available)} шт`}
    </span>
  );
  if (skus.length <= 1 || !interactive) return body;

  return (
    <Popover>
      <PopoverTrigger className="underline decoration-dotted underline-offset-4" aria-label="Остаток по фасовкам">
        {body}
      </PopoverTrigger>
      <PopoverContent align="end" className="min-w-[240px]">
        <p className="type-caption mb-1.5 text-on-surface-variant">Доступно по фасовкам</p>
        <ul className="type-body flex flex-col gap-1">
          {skus.map((s) => (
            <li key={s.skuId} className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate">{s.displayName?.trim() || s.code}</span>
              <span className={cn("shrink-0 tabular-nums", TONE_TEXT[toneOf(Number(s.available), s.isLow)])}>
                {plainQty(s.available)}
                {s.minStock ? ` · порог ${plainQty(s.minStock)}` : ""}
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
